"use client";

import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TypographyH1, TypographyP } from "@/components/ui/typography";
import { fallbackLng } from "@/lib/i18n/settings";
import { findRelatedJobs } from "@/lib/utils";
import jobsList from "@/public/data/jobsList.json";
import copy from "copy-to-clipboard";
import { Check, ChevronDown, ChevronUp, Copy, Share2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import showdown from "showdown";
import { HeaderBg } from "../HeaderBg";
import { resumeModules } from "./ResumeExamplesCategoriesSection";

const converter = new showdown.Converter({
  simpleLineBreaks: true,
  literalMidWordUnderscores: true,
  strikethrough: true,
  tables: true,
});

interface ResumeExampleData {
  overview: string;
  about: string;
  introduction: string;
  items: Array<{
    id: string;
    jobSlug: number;
    resumeModuleSlug: number;
    title: string;
    content: string;
    grade: keyof typeof gradeToLevel; // 更新类型为字符串字面量联合类型
    createdAt: string;
  }>;
}

const gradeToLevel = {
  ENTRY_LEVEL: { text: "Entry Level", bg: "bg-blue-400" },
  JUNIOR: { text: "Junior", bg: "bg-blue-500" },
  EXPERIENCED: { text: "Experienced", bg: "bg-emerald-500" },
  SENIOR: { text: "Senior", bg: "bg-purple-500" },
  ADVANCED: { text: "Advanced", bg: "bg-purple-600" },
} as const;

export default function ResumeExamplesDetailSection({
  lng = fallbackLng,
  job = "",
  module = "",
  data = {
    overview: "",
    about: "",
    introduction: "",
    items: [],
  },
}: {
  lng?: string;
  job?: string;
  module?: string;
  data: ResumeExampleData;
}) {
  const [mounted, setMounted] = useState(false);
  const [showAll, setShowAll] = useState(true);

  useEffect(() => {
    setMounted(true);
    setShowAll(false);
  }, []);

  const displayedExamples =
    !mounted || showAll ? data?.items || [] : (data?.items || []).slice(0, 3);
  const hasMoreExamples = (data?.items?.length || 0) > 3;
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const formatJobTitle = (slug: string) => {
    return decodeURIComponent(slug)
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const jobTitle = formatJobTitle(job);
  const moduleInfo = resumeModules.find((m) => m.slug === module);
  return (
    <div>
      <HeaderBg>
        <div className="flex h-[322px] flex-col items-center justify-center px-4 text-center">
          <TypographyH1 className=" sm:text-2xl md:text-4xl">
            {jobTitle}
          </TypographyH1>
          <TypographyP className="mt-[10px]  font-bold sm:text-2xl  md:text-4xl lg:text-5xl">
            Resume <span className="text-emerald-600">{moduleInfo?.name}</span>{" "}
            Examples & Samples
          </TypographyP>
        </div>
      </HeaderBg>

      <div className="mx-auto mt-[-40px]  w-fit  max-w-[1280px] px-4">
        <div className="relative z-10 mx-auto flex flex-col gap-6">
          <div className="flex w-full justify-center">
            <Card className="w-full rounded-2xl border-none shadow-lg sm:w-fit">
              <div className="flex flex-col items-center justify-between p-3 sm:flex-row">
                <div className="relative w-full sm:hidden">
                  <Select
                    value={module}
                    onValueChange={(value) => {
                      window.location.href = `/resume-examples/${job}/${value}`;
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={moduleInfo?.name || "Select a module"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {resumeModules.map((category) => (
                        <SelectItem key={category.id} value={category.slug}>
                          <div className="flex items-center gap-2">
                            <category.icon className="h-4 w-4" />
                            <span>{category.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="hidden w-full overflow-x-auto sm:block sm:w-auto">
                  <div className="flex items-center gap-1.5">
                    {resumeModules.map((category) => {
                      const Icon = category.icon;
                      const isSelected = category.slug === module;
                      return (
                        <Link
                          key={category.id}
                          href={`/resume-examples/${job}/${category.slug}`}
                          className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-1.5 transition-all hover:bg-emerald-50 ${
                            isSelected
                              ? "bg-emerald-100 text-emerald-600"
                              : "text-gray-600 hover:text-emerald-600"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="whitespace-nowrap text-sm font-medium">
                            {category.name}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>

                <Link
                  href="/home"
                  className="mt-3 w-full rounded-xl bg-gradient-to-r from-[#0A4145] via-[#3E986C] via-[#A7B542] to-[#E1A602] px-6 py-2.5 text-center text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 sm:ml-2 sm:mt-0 sm:w-auto"
                  style={{
                    background:
                      "linear-gradient(101deg, #0A4145 -3.56%, #3E986C 36.5%, #A7B542 70.07%, #E1A602 104.71%)",
                  }}
                >
                  Create Your Resume
                </Link>
              </div>
            </Card>
          </div>

          <div className="mt-8 flex flex-col gap-[250px] lg:flex-row">
            <div className="flex-1 space-y-6 sm:space-y-8">
              <div className="flex flex-col">
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <div className="h-[20px] w-2 bg-emerald-500"></div>
                  <h4 className="text-lg font-bold sm:text-xl">
                    Overview of {jobTitle}
                  </h4>
                </div>
                <div
                  className="text-sm leading-relaxed text-gray-600 sm:text-base"
                  dangerouslySetInnerHTML={{
                    __html: converter.makeHtml(
                      data.overview.replace(/\n/g, "<br/>"),
                    ),
                  }}
                />
              </div>

              <div className="flex flex-col">
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <div className="h-[20px] w-2 bg-emerald-500"></div>
                  <h4 className="text-lg font-bold sm:text-xl">
                    About {jobTitle} Resume
                  </h4>
                </div>
                <div
                  className="text-sm leading-relaxed text-gray-600 sm:text-base"
                  dangerouslySetInnerHTML={{
                    __html: converter.makeHtml(
                      data.about.replace(/\n/g, "<br/>"),
                    ),
                  }}
                />
              </div>

              <div className="flex flex-col">
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <div className="h-[20px] w-2 bg-emerald-500"></div>
                  <h4 className="text-lg font-bold sm:text-xl">
                    Introduction to {jobTitle} Resume {moduleInfo?.name}
                  </h4>
                </div>
                <div
                  className="text-sm leading-relaxed text-gray-600 sm:text-base"
                  dangerouslySetInnerHTML={{
                    __html: converter.makeHtml(
                      data.introduction.replace(/\n/g, "<br/>"),
                    ),
                  }}
                />
              </div>

              <div className="mt-8 sm:mt-12">
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <div className="h-[20px] w-2 bg-emerald-500"></div>
                  <h4 className="text-lg font-bold sm:text-xl">
                    Examples & Samples of {jobTitle} Resume {moduleInfo?.name}
                  </h4>
                </div>
                <div className="space-y-4 sm:space-y-6">
                  {displayedExamples.map((example) => (
                    <div
                      key={example.id}
                      className="relative rounded-xl border border-[rgba(176,228,170,0.50)] bg-[#F2FAF1] p-4 sm:p-6"
                    >
                      <div className="absolute right-3 top-3 sm:right-4 sm:top-4">
                        <span
                          className={`${gradeToLevel[example.grade]?.bg} rounded-sm px-2 py-0.5 text-xs font-medium text-white sm:px-3 sm:py-1`}
                        >
                          {gradeToLevel[example.grade]?.text}
                        </span>
                      </div>

                      <h3 className="mb-3 text-base font-semibold sm:mb-4 sm:text-xl">
                        {example.title}
                      </h3>
                      <div
                        className="mb-4 text-sm text-gray-700 sm:mb-6 sm:text-base"
                        dangerouslySetInnerHTML={{
                          __html: converter.makeHtml(
                            example.content.replace(/\n/g, "<br/>"),
                          ),
                        }}
                      />
                      <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
                        <button
                          onClick={() => {
                            copy(example.content);
                            setCopiedId(example.id);
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          className="flex items-center justify-center gap-2 rounded-full border border-[rgba(176,228,170,0.50)] bg-white px-4 py-2 text-sm font-medium text-emerald-600 shadow-sm transition-all hover:border-emerald-200 hover:bg-emerald-50 hover:shadow"
                        >
                          {copiedId === example.id ? (
                            <>
                              <Check className="h-4 w-4" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4" />
                              Copy to clipboard
                            </>
                          )}
                        </button>
                        <Link
                          href="/home"
                          onClick={() => {
                            copy(example.content);
                          }}
                          className="flex items-center justify-center gap-2 rounded-full border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:border-emerald-700 hover:bg-emerald-700 hover:shadow"
                        >
                          <Share2 className="h-4 w-4" />
                          Copy to Talencat
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>

                {mounted && hasMoreExamples && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={() => setShowAll(!showAll)}
                      className="group flex cursor-pointer items-center gap-2 rounded-full border border-[rgba(176,228,170,0.50)] bg-[#F2FAF1] px-8 py-2.5 text-emerald-600 transition-all hover:border-emerald-200 hover:bg-emerald-50"
                    >
                      {showAll ? (
                        <>
                          View Less
                          <ChevronUp className="h-4 w-4 transition-transform group-hover:-translate-y-0.5" />
                        </>
                      ) : (
                        <>
                          View More
                          <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="hidden lg:block lg:w-[300px]">
              <div className="sticky top-[100px] space-y-4">
                <div className="overflow-hidden rounded-[16px] bg-white shadow-lg">
                  <Image
                    src="/images/resume-builder.png"
                    alt="Resume Builder"
                    className="object-cover"
                    width={299}
                    height={200}
                  />

                  <div className="p-6">
                    <div className="mb-6 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                          <Check className="h-3 w-3 text-emerald-600" />
                        </div>
                        <span className="text-sm text-gray-600">
                          ATS-optimized Resume
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                          <Check className="h-3 w-3 text-emerald-600" />
                        </div>
                        <span className="text-sm text-gray-600">
                          Well-designed Templates
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                          <Check className="h-3 w-3 text-emerald-600" />
                        </div>
                        <span className="text-sm text-gray-600">
                          Writing Guide Available
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                          <Check className="h-3 w-3 text-emerald-600" />
                        </div>
                        <span className="text-sm text-gray-600">
                          Powered by Generative AI
                        </span>
                      </div>
                    </div>

                    <Link
                      href="/home"
                      className="inline-block w-full rounded-full bg-gradient-to-r from-[#FBD747] to-[#2FAC3B] px-5 py-2.5 text-center text-sm font-medium text-white shadow-[0px_2px_12px_0px_rgba(12,18,48,0.04)] transition-all hover:opacity-90"
                    >
                      Start for Free
                    </Link>
                  </div>
                </div>

                <div className="mt-4 rounded-[12px] bg-white p-4 shadow-lg">
                  <h3 className="mb-3 text-base font-medium text-gray-900">
                    View Examples for Other Jobs：
                  </h3>
                  <div className="space-y-2">
                    {findRelatedJobs(jobTitle, jobsList, 5).map(
                      (relatedJob, index) => (
                        <Link
                          key={index}
                          href={`/resume-examples/${relatedJob
                            .toLowerCase()
                            .replace(/\s+/g, "-")
                            .replace(/-+$/, "")}/${module}`}
                          className="flex items-center gap-1.5 rounded-md py-1 text-gray-600 transition-colors hover:text-emerald-600"
                        >
                          <div className="h-1 w-1 rounded-full bg-emerald-500"></div>
                          <span className="text-xs font-medium">
                            {relatedJob}
                          </span>
                        </Link>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
